// Shared dashboard token-row derivation.
//
// Extracted out of the mobile-web `useHomeDashboard` so the extension's full-tab
// dashboard side panel can render the SAME rows (portfolio-allocation donut)
// without pulling in that hook's screen-level state and side effects (mode
// switching, network-filter invariants, hide-token flow, activity session…).
// `useHomeDashboard` consumes this hook, so there is exactly one copy of the
// filter/sort/format rules.
import { useCallback, useMemo } from 'react'

import { SelectedAccountPortfolioTokenResult } from '@ambire-common/interfaces/selectedAccount'
import { AssetType } from '@ambire-common/libs/defiPositions/types'
import { getTokenAmount, getTokenBalanceInUSD } from '@ambire-common/libs/portfolio/helpers'
import { TokenResult } from '@ambire-common/libs/portfolio/interfaces'
import {
  getNetworkIdsWithPriceError,
  splitDustTokens
} from '@common/modules/dashboard/helpers/dustTokens'
import getAndFormatTokenDetails from '@common/modules/dashboard/helpers/getTokenDetails'
import { tokenOrCollectionSearch } from '@common/utils/search'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import usePortfolioControllerState from '@web/hooks/usePortfolioControllerState/usePortfolioControllerState'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useRailgunTokens from '@web/hooks/useRailgunTokens/useRailgunTokens'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { getMissingPOIUsdByToken, priceKey } from '@web/utils/railgunTokens'
import { getTokenId } from '@web/utils/token'

export type AssetRowData = {
  id: string
  symbol: string
  network: string
  chainId: bigint
  address: string
  decimals: number
  balanceFormatted: string
  balanceUSDFormatted: string
  priceUSDFormatted: string
  /** 24h price change percent (signed), or null when unknown. */
  deltaPct: number | null
  /** Private mode only: USD value of this token's funds still awaiting POI
   * verification (the per-token slice of the "Incoming $X" pill). 0 otherwise. */
  pendingPOIUsd: number
  raw: SelectedAccountPortfolioTokenResult
}

const hasAmount = (token: SelectedAccountPortfolioTokenResult) =>
  token.amount > 0n || (token.amountPostSimulation != null && token.amountPostSimulation > 0n)

const get24hChange = (token: SelectedAccountPortfolioTokenResult): number | null => {
  // `marketDataIn` (like `priceIn`) can arrive as a non-array on the mobile-web
  // worker→page path (iOS Safari); guard so `.find` can't white-screen the dashboard.
  const marketDataIn = (Array.isArray(token.marketDataIn) ? token.marketDataIn : []) as {
    baseCurrency?: string
    usd_24h_change?: number
  }[]
  const usd = marketDataIn.find((m) => m.baseCurrency?.toLowerCase() === 'usd')
  return typeof usd?.usd_24h_change === 'number' ? usd.usd_24h_change : null
}

type Params = {
  /** Private (Railgun) mode sources the rows from the railgun balance maps. */
  isPrivate: boolean
  /** Optional free-text filter, wired to the dashboard search input. */
  searchValue?: string
}

const useDashboardAssets = ({ isPrivate, searchValue }: Params) => {
  const { portfolio, dashboardNetworkFilter, balanceAffectingErrors } =
    useSelectedAccountControllerState()
  // PortfolioController.tokenPreferences is updated + emitted synchronously by
  // `toggleHideToken` (before the network re-fetch that actually stamps
  // token.flags.isHidden). The dashboard row otherwise stays visible until that
  // re-fetch lands — and on a slow/erroring chain (e.g. PulseChain) it may never
  // land, leaving a "hidden" token on screen. Filter against the preference
  // directly so the row disappears the instant the user confirms Hide, and
  // reappears the instant they un-hide from Settings.
  const portfolioCtrl = usePortfolioControllerState()
  const tokenPreferences = (portfolioCtrl?.tokenPreferences || []) as {
    address: string
    chainId: bigint
    isHidden?: boolean
  }[]
  // User-added (custom) tokens. These must stay on the dashboard even at a 0
  // balance — the only way to remove one is the explicit "Hide token" action.
  const customTokens = (portfolioCtrl?.customTokens || []) as {
    address: string
    chainId: bigint
  }[]
  const { networks } = useExtensionAwareNetworks()
  const { networks: allNetworks } = useNetworksControllerState()
  const { currentRailgunWallet } = useRailgunContext()
  const railgunTokens = useRailgunTokens()

  // Per-token pending-POI USD (private mode): the slice of the "Incoming $X"
  // pill that belongs to each token, so each row can show a "+$X pending POI"
  // subscript. Mirrors the extension popup's BaseTokenItem.
  const pendingPOIByToken = useMemo<Map<string, number>>(() => {
    if (!isPrivate) return new Map()
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    if (!prices) return new Map()
    return getMissingPOIUsdByToken(
      currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
      currentRailgunWallet?.missingExternalPOIBalanceTokenMap,
      prices
    )
  }, [
    isPrivate,
    currentRailgunWallet?.balanceTokenPriceMap,
    currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
    currentRailgunWallet?.missingExternalPOIBalanceTokenMap
  ])

  // Set of `address-chainId` keys the user has hidden, derived straight from the
  // controller preference (authoritative + synchronous). Lower-cased address and
  // stringified chainId so it survives bigint/string drift across the worker.
  const hiddenPreferenceKeys = useMemo(() => {
    const set = new Set<string>()
    tokenPreferences.forEach((pref) => {
      if (pref.isHidden) set.add(`${pref.address?.toLowerCase()}-${pref.chainId?.toString()}`)
    })
    return set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPreferences])

  // Set of `address-chainId` keys the user manually added. Same key shape as
  // hiddenPreferenceKeys so the bigint/string drift across the worker is handled.
  const customTokenKeys = useMemo(() => {
    const set = new Set<string>()
    customTokens.forEach((t) => set.add(`${t.address?.toLowerCase()}-${t.chainId?.toString()}`))
    return set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTokens])

  const sortedTokens = useMemo<SelectedAccountPortfolioTokenResult[]>(() => {
    const source = (
      isPrivate ? railgunTokens : portfolio?.tokens || []
    ) as SelectedAccountPortfolioTokenResult[]
    const filtered = [...source]
      .filter((token) => {
        if (token.flags.isHidden) return false
        if (
          hiddenPreferenceKeys.has(`${token.address?.toLowerCase()}-${token.chainId?.toString()}`)
        )
          return false
        if (token.flags.onGasTank) return false
        if (token.flags.defiTokenType === AssetType.Borrow) return false
        if (
          dashboardNetworkFilter &&
          dashboardNetworkFilter !== 'rewards' &&
          dashboardNetworkFilter !== 'gasTank'
        ) {
          if (token.chainId?.toString() !== dashboardNetworkFilter.toString()) return false
        }
        if (hasAmount(token)) return true
        // Keep user-added (custom) tokens even at a 0 balance — they only leave
        // the dashboard via the explicit "Hide token" action (checked above).
        if (customTokenKeys.has(`${token.address?.toLowerCase()}-${token.chainId?.toString()}`))
          return true
        // Keep pending-only tokens (0 spendable but funds awaiting POI) so the
        // user sees which token the "Incoming $X" pill refers to. useRailgunTokens
        // already adds these as amount-0 rows; without this they're filtered out.
        return (pendingPOIByToken.get(priceKey(Number(token.chainId), token.address)) ?? 0) > 0
      })
      .sort((a, b) => {
        const diff = getTokenBalanceInUSD(b) - getTokenBalanceInUSD(a)
        if (diff !== 0) return diff
        return Number(getTokenAmount(b)) - Number(getTokenAmount(a))
      })

    if (!searchValue) return filtered as SelectedAccountPortfolioTokenResult[]

    return tokenOrCollectionSearch({
      networks: allNetworks,
      assets: filtered as TokenResult[],
      search: searchValue,
      searchType: 'token'
    }) as SelectedAccountPortfolioTokenResult[]
  }, [
    isPrivate,
    railgunTokens,
    portfolio?.tokens,
    dashboardNetworkFilter,
    searchValue,
    allNetworks,
    pendingPOIByToken,
    hiddenPreferenceKeys,
    customTokenKeys
  ])

  // Dust/spam grouping (public mode only): tokens with a balance but no real
  // USD price — or worth less than a cent — get collapsed behind an
  // "N other tokens" row instead of polluting the main list (scam airdrops
  // discovered by the portfolio hints after any on-chain activity). Searching
  // bypasses the grouping so dust tokens stay findable.
  const networkIdsWithPriceError = useMemo(
    () => getNetworkIdsWithPriceError(balanceAffectingErrors, allNetworks),
    [balanceAffectingErrors, allNetworks]
  )

  const { visibleTokens, dustTokens } = useMemo(() => {
    if (isPrivate || searchValue) {
      return { visibleTokens: sortedTokens, dustTokens: [] as SelectedAccountPortfolioTokenResult[] }
    }
    return splitDustTokens(sortedTokens, networkIdsWithPriceError, (token) => {
      // Custom tokens here are a controller list (token.flags.isCustom isn't
      // stamped on the mobile-web path), and pending-POI rows must stay visible
      // so the "Incoming $X" pill has a row to point at.
      if (customTokenKeys.has(`${token.address?.toLowerCase()}-${token.chainId?.toString()}`))
        return true
      return (pendingPOIByToken.get(priceKey(Number(token.chainId), token.address)) ?? 0) > 0
    })
  }, [
    isPrivate,
    searchValue,
    sortedTokens,
    networkIdsWithPriceError,
    customTokenKeys,
    pendingPOIByToken
  ])

  const toAssetRow = useCallback(
    (token: SelectedAccountPortfolioTokenResult): AssetRowData => {
      const details = getAndFormatTokenDetails(token, networks)
      return {
        id: getTokenId(token),
        symbol: token.symbol,
        network: details.networkData?.name || '',
        chainId: token.chainId,
        address: token.address,
        decimals: token.decimals,
        balanceFormatted: details.balanceFormatted,
        balanceUSDFormatted: details.balanceUSDFormatted,
        priceUSDFormatted: details.priceUSDFormatted,
        deltaPct: get24hChange(token),
        pendingPOIUsd: pendingPOIByToken.get(priceKey(Number(token.chainId), token.address)) ?? 0,
        raw: token
      }
    },
    [networks, pendingPOIByToken]
  )

  const assets = useMemo<AssetRowData[]>(() => visibleTokens.map(toAssetRow), [
    visibleTokens,
    toAssetRow
  ])

  const dustAssets = useMemo<AssetRowData[]>(() => dustTokens.map(toAssetRow), [
    dustTokens,
    toAssetRow
  ])

  const dustTotalUSD = useMemo(
    () => dustTokens.reduce((sum, token) => sum + getTokenBalanceInUSD(token), 0),
    [dustTokens]
  )

  return { assets, dustAssets, dustTotalUSD, sortedTokens, pendingPOIByToken }
}

export default useDashboardAssets
